import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Alert,
    CircularProgress,
    Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Formik, Form, Field } from 'formik';
import TextField from '../TextField';
import useAPI from '../../hooks/useAPI';
import { useUpdateUser } from '../../utils/contexts/app-context';

// Formik-compatible TextField wrapper
const FormikTextField = ({ field, form, ...props }: any) => {
    const { name } = field;
    const { errors, touched } = form;
    return (
        <TextField
            {...field}
            {...props}
            error={errors[name]}
            touched={touched[name]}
        />
    );
};

interface ChangePasswordDialogProps {
    open: boolean;
    onClose: () => void;
}

const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({ open, onClose }) => {
    const { t } = useTranslation();
    const API = useAPI();
    const updateUser = useUpdateUser();
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const validationSchema = Yup.object().shape({
        oldPassword: Yup.string().required(t('validation:errors.required') || 'Required'),
        newPassword: Yup.string()
            .min(8, t('validation:errors.password_min') || 'Password must be at least 8 characters')
            .required(t('validation:errors.required') || 'Required'),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref('newPassword')], t('validation:errors.password_match') || 'Passwords must match')
            .required(t('validation:errors.required') || 'Required'),
    });

    const initialValues = {
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
    };

    const handleSubmit = async (values: typeof initialValues) => {
        setError(null);
        setLoading(true);

        try {
            await API.post('/auth/change-password', {
                oldPassword: values.oldPassword,
                newPassword: values.newPassword,
            });

            // Refresh user data to clear passwordSetByAdmin flag
            if (updateUser) {
                await updateUser(true);
            }

            onClose();
        } catch (err: any) {
            console.error('Error changing password:', err);
            setError(
                err?.response?.data?.message || 
                err?.message || 
                t('sentences:pages.profile.password_change_error') || 
                'Failed to change password'
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => {}} maxWidth="sm" fullWidth>
            <DialogTitle>
                {t('sentences:pages.profile.change_password_required') || 'Change Your Password'}
            </DialogTitle>
            <DialogContent>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {t('sentences:pages.profile.password_set_by_admin') || 
                        'Your password was set by an administrator. Please change it now for security.'}
                </Alert>

                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {(formik) => (
                        <Form>
                            <Box sx={{ mt: 2 }}>
                                {error && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {error}
                                    </Alert>
                                )}

                                <Box sx={{ mb: 2 }}>
                                    <Field
                                        component={FormikTextField}
                                        name="oldPassword"
                                        type="password"
                                        label={t('nouns.current_password') || 'Current Password'}
                                        fullWidth
                                    />
                                </Box>

                                <Box sx={{ mb: 2 }}>
                                    <Field
                                        component={FormikTextField}
                                        name="newPassword"
                                        type="password"
                                        label={t('nouns.new_password') || 'New Password'}
                                        fullWidth
                                    />
                                </Box>

                                <Box sx={{ mb: 2 }}>
                                    <Field
                                        component={FormikTextField}
                                        name="confirmPassword"
                                        type="password"
                                        label={t('nouns.confirm_password') || 'Confirm Password'}
                                        fullWidth
                                    />
                                </Box>

                                <DialogActions sx={{ px: 0, mt: 2 }}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={loading || !formik.isValid}
                                    >
                                        {loading ? (
                                            <>
                                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                                Changing...
                                            </>
                                        ) : (
                                            'Change Password'
                                        )}
                                    </Button>
                                </DialogActions>
                            </Box>
                        </Form>
                    )}
                </Formik>
            </DialogContent>
        </Dialog>
    );
};

export default ChangePasswordDialog;

